/**
 * 🎫 DescargaSorteoPage
 * Formulario público de código de sorteo (feria).
 *
 * Ruta: /expande/app/descarga-oficial/sorteo
 * Sin autenticación. Usa publicApi (sin Bearer).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logoExpande from "../../assets/expande-logo.png";
import quilloUb from "../../assets/quillo_ub.jpeg";
import { registrarFeria } from "../../services/FeriaPublicService";
import SorteoBasesModal from "./SorteoBasesModal";
import { SORTEO_LOCATION_ADDRESS, SORTEO_MAPS_URL } from "./sorteoPublicCopy";
import {
  SORTEO_PAIS_DEFAULT,
  validateSorteoForm,
} from "./sorteoFormValidation";
import "./DescargaAppPage.css";

const INITIAL_FORM = {
  nombres: "",
  apellidos: "",
  email: "",
  whatsappCelular: "",
  pais: SORTEO_PAIS_DEFAULT,
  departamento: "",
  ciudad: "",
};

const PRIVACY_POLICY_URL =
  "https://www.herbalife.com.bo/footer-pages/politica-privacidad/";
const CONSENT_CONTACT_EMAIL = "soportemisterquillo@gmail.com";
const CONSENT_CONTACT_PHONE = "67816517";
const CONSENT_ERROR =
  "Debes aceptar el tratamiento de tus datos personales para participar.";

export default function DescargaSorteoPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [basesOpen, setBasesOpen] = useState(false);
  const [aceptaTratamientoDatos, setAceptaTratamientoDatos] = useState(false);
  const [consentError, setConsentError] = useState("");
  // Opcional: no bloquea el registro.
  const [aceptaComunicacionesComerciales, setAceptaComunicacionesComerciales] =
    useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Código de sorteo | EXPANDE";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const openBases = () => setBasesOpen(true);
  const closeBases = () => setBasesOpen(false);

  const onChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormError("");
  };

  const onConsentChange = (event) => {
    setAceptaTratamientoDatos(event.target.checked);
    if (event.target.checked) setConsentError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const { values, errors, isValid } = validateSorteoForm(form);
    setFieldErrors(errors);
    setConsentError(aceptaTratamientoDatos ? "" : CONSENT_ERROR);
    if (!isValid || !aceptaTratamientoDatos) return;

    setSubmitting(true);
    setFormError("");

    try {
      const { data } = await registrarFeria({
        ...values,
        aceptaTratamientoDatos: true,
        aceptaComunicacionesComerciales,
      });
      setResultado({
        codigoSorteo: data?.codigoSorteo,
        alreadyExists: Boolean(data?.alreadyExists),
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "No se pudo generar el código. Intenta de nuevo.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="expdl-page">
      <main className="expdl-card">
        <div className="expdl-logo">
          <img src={logoExpande} alt="EXPANDE" />
        </div>

        {resultado?.codigoSorteo ? (
          <div className="expdl-code-result" role="status">
            {resultado.alreadyExists ? (
              <p className="expdl-code-hint">
                Ya tenías un código generado. Este es tu código.
              </p>
            ) : null}
            <p className="expdl-code-label">Tu código es:</p>
            <p className="expdl-code-value">{resultado.codigoSorteo}</p>

            <div className="expdl-location-card">
              <div className="expdl-location-media">
                <img
                  src={quilloUb}
                  alt="Fachada de Protein Bar Mr. Quillo"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="expdl-stand-banner">
                Acércate a Protein Bar Mr. Quillo para validar tu código.
              </p>
              <p className="expdl-location-address">{SORTEO_LOCATION_ADDRESS}</p>
              <a
                className="expdl-maps-btn"
                href={SORTEO_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Cómo llegar
              </a>
            </div>

            <p className="expdl-code-note">
              Guarda este código. Solo se permite un código por correo o número de
              celular.
            </p>

            <button
              type="button"
              className="expdl-bases-trigger"
              onClick={openBases}
            >
              Ver bases y condiciones
            </button>

            <Link
              className="expdl-back-link"
              to="/expande/app/descarga-oficial"
            >
              Ir a descargar la app
            </Link>
          </div>
        ) : (
          <form className="expdl-form" onSubmit={handleSubmit} noValidate>
            <h1 className="expdl-title expdl-sorteo-title">
              Obtén tu código
              <br />
              de <span className="expdl-accent">sorteo</span>
            </h1>
            <p className="expdl-subtitle">
              Completa tus datos reales. Si resultas ganador o necesitamos contactarte,
              usaremos esta información.
            </p>

            <label className="expdl-field">
              <span>Nombres</span>
              <input
                type="text"
                name="nombres"
                autoComplete="given-name"
                value={form.nombres}
                onChange={onChange("nombres")}
                aria-invalid={Boolean(fieldErrors.nombres)}
                required
              />
              {fieldErrors.nombres ? (
                <span className="expdl-field-error">{fieldErrors.nombres}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>Apellidos</span>
              <input
                type="text"
                name="apellidos"
                autoComplete="family-name"
                value={form.apellidos}
                onChange={onChange("apellidos")}
                aria-invalid={Boolean(fieldErrors.apellidos)}
                required
              />
              {fieldErrors.apellidos ? (
                <span className="expdl-field-error">{fieldErrors.apellidos}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>Correo</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={onChange("email")}
                aria-invalid={Boolean(fieldErrors.email)}
                required
              />
              {fieldErrors.email ? (
                <span className="expdl-field-error">{fieldErrors.email}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>WhatsApp / celular</span>
              <input
                type="tel"
                name="whatsappCelular"
                autoComplete="tel"
                inputMode="tel"
                placeholder="Ej: +59173429001"
                value={form.whatsappCelular}
                onChange={onChange("whatsappCelular")}
                aria-invalid={Boolean(fieldErrors.whatsappCelular)}
                required
              />
              {fieldErrors.whatsappCelular ? (
                <span className="expdl-field-error">{fieldErrors.whatsappCelular}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>País</span>
              <input
                type="text"
                name="pais"
                autoComplete="country-name"
                value={form.pais}
                onChange={onChange("pais")}
                aria-invalid={Boolean(fieldErrors.pais)}
                required
              />
              {fieldErrors.pais ? (
                <span className="expdl-field-error">{fieldErrors.pais}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>Departamento / Estado / Región</span>
              <input
                type="text"
                name="departamento"
                autoComplete="address-level1"
                placeholder="Ej: Santa Cruz, Buenos Aires, California"
                value={form.departamento}
                onChange={onChange("departamento")}
                aria-invalid={Boolean(fieldErrors.departamento)}
                required
              />
              {fieldErrors.departamento ? (
                <span className="expdl-field-error">{fieldErrors.departamento}</span>
              ) : null}
            </label>

            <label className="expdl-field">
              <span>Ciudad / municipio</span>
              <input
                type="text"
                name="ciudad"
                autoComplete="address-level2"
                value={form.ciudad}
                onChange={onChange("ciudad")}
                aria-invalid={Boolean(fieldErrors.ciudad)}
                required
              />
              {fieldErrors.ciudad ? (
                <span className="expdl-field-error">{fieldErrors.ciudad}</span>
              ) : null}
            </label>

            <div className="expdl-consent" data-testid="sorteo-consentimiento">
              <p className="expdl-consent-text">
                <strong className="expdl-consent-title">
                  Protección de datos personales:
                </strong>{" "}
                Al participar, autorizas a Jose Maria Rodriguez Molino a tratar los
                datos que proporciones únicamente para gestionar el sorteo,
                verificar requisitos, contactar a la persona ganadora y entregar el
                premio.
              </p>
              <p className="expdl-consent-text">
                Tus datos serán tratados de forma confidencial, con medidas
                razonables de seguridad, y se conservarán solo durante el tiempo
                necesario para estas finalidades y obligaciones aplicables. No se
                usarán para otras finalidades, incluidas finalidades comerciales, salvo
                autorización expresa y separada.
              </p>
              <p className="expdl-consent-text">
                Consultas:{" "}
                <a
                  className="expdl-consent-link"
                  href={`mailto:${CONSENT_CONTACT_EMAIL}`}
                >
                  {CONSENT_CONTACT_EMAIL}
                </a>{" "}
                / {CONSENT_CONTACT_PHONE}. Consulta la{" "}
                <a
                  className="expdl-consent-link"
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política de Privacidad de Herbalife Bolivia
                </a>
                .
              </p>
              <div className="expdl-consent-check">
                <input
                  type="checkbox"
                  id="sorteo-acepta-tratamiento-datos"
                  name="aceptaTratamientoDatos"
                  checked={aceptaTratamientoDatos}
                  onChange={onConsentChange}
                  aria-invalid={Boolean(consentError)}
                  aria-describedby={
                    consentError ? "sorteo-acepta-tratamiento-datos-error" : undefined
                  }
                  required
                />
                <label htmlFor="sorteo-acepta-tratamiento-datos">
                  He leído y autorizo el tratamiento de mis datos personales para las
                  finalidades indicadas.
                </label>
              </div>
              {consentError ? (
                <span
                  id="sorteo-acepta-tratamiento-datos-error"
                  className="expdl-field-error"
                >
                  {consentError}
                </span>
              ) : null}
            </div>

            <div
              className="expdl-consent expdl-consent--optional"
              data-testid="sorteo-comunicaciones-comerciales"
            >
              <span className="expdl-consent-badge">Opcional</span>
              <div className="expdl-consent-check">
                <input
                  type="checkbox"
                  id="sorteo-acepta-comunicaciones-comerciales"
                  name="aceptaComunicacionesComerciales"
                  checked={aceptaComunicacionesComerciales}
                  onChange={(event) =>
                    setAceptaComunicacionesComerciales(event.target.checked)
                  }
                />
                <label htmlFor="sorteo-acepta-comunicaciones-comerciales">
                  Acepto recibir información comercial, novedades, ofertas y
                  promociones de Mr. Quillo. Entiendo que esta autorización es
                  opcional y que puedo retirarla posteriormente.
                </label>
              </div>
            </div>

            {formError ? (
              <p className="expdl-form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <button
              type="submit"
              className="expdl-submit"
              disabled={submitting}
            >
              {submitting ? "Generando…" : "Generar código"}
            </button>

            <p className="expdl-form-accept">
              Al generar tu código, aceptas las{" "}
              <button
                type="button"
                className="expdl-inline-link"
                onClick={openBases}
              >
                bases y condiciones
              </button>{" "}
              del sorteo.
            </p>

            <Link
              className="expdl-back-link"
              to="/expande/app/descarga-oficial"
            >
              Ir a descargar la app
            </Link>
          </form>
        )}
      </main>

      <SorteoBasesModal open={basesOpen} onClose={closeBases} />
    </div>
  );
}
